import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { MessageSquare, Send, Check, CheckCheck, ImagePlus, X, ArrowLeft, Loader2, Receipt, CreditCard, Download, FileText, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { resolveAssetUrl } from '../lib/assets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const initials = (name = '') => name.trim().charAt(0).toUpperCase() || '?';

export default function MessagesPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toId = searchParams.get('to');
  const toName = searchParams.get('name');
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [presence, setPresence] = useState({});
  const [lastSeen, setLastSeen] = useState({});
  const [pendingFile, setPendingFile] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invAmount, setInvAmount] = useState('');
  const [invDesc, setInvDesc] = useState('');
  const [invSubmitting, setInvSubmitting] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/wallet`).then(({ data }) => setWalletBalance(data.balance || 0)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate('/welcome');
      return;
    }
    fetchMessages();
  }, [user]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/messages`);
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group messages by conversation
  const conversations = messages.reduce((acc, msg) => {
    const partnerId = msg.sender_id === user?._id ? msg.recipient_id : msg.sender_id;
    const partnerName = msg.sender_id === user?._id ? msg.recipient_name : msg.sender_name;
    
    if (!acc[partnerId]) {
      acc[partnerId] = {
        partnerId,
        partnerName,
        messages: [],
        lastMessage: null,
        unreadCount: 0
      };
    }
    acc[partnerId].messages.push(msg);
    if (!acc[partnerId].lastMessage || new Date(msg.created_at) > new Date(acc[partnerId].lastMessage.created_at)) {
      acc[partnerId].lastMessage = msg;
    }
    if (!msg.is_read && msg.recipient_id === user?._id) {
      acc[partnerId].unreadCount++;
    }
    return acc;
  }, {});

  const conversationsList = Object.values(conversations).sort((a, b) => 
    new Date(b.lastMessage?.created_at) - new Date(a.lastMessage?.created_at)
  );

  // Preselect (or create a placeholder for) a conversation when arriving with
  // ?to=<userId>&name=<name> — e.g. the footer "Write to admin" button.
  const hasPlaceholder = toId && !conversations[toId];
  const displayList = hasPlaceholder
    ? [{ partnerId: toId, partnerName: toName || (language === 'lv' ? 'KoLV Admins' : 'KoLV Admin'), messages: [], lastMessage: null, unreadCount: 0 }, ...conversationsList]
    : conversationsList;

  useEffect(() => {
    if (loading) return;
    if (toId && !selectedConversation) {
      const existing = conversations[toId];
      setSelectedConversation(existing || { partnerId: toId, partnerName: toName || (language === 'lv' ? 'KoLV Admins' : 'KoLV Admin'), messages: [], lastMessage: null, unreadCount: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, toId, messages]);

  // Keep the open thread in sync with freshly fetched messages (so a message
  // sent into a brand-new conversation appears immediately in the thread).
  useEffect(() => {
    if (!selectedConversation) return;
    const fresh = conversations[selectedConversation.partnerId];
    if (fresh) setSelectedConversation(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Presence ("online" dots): poll every 20s for all conversation partners.
  const partnerIds = conversationsList.map((c) => c.partnerId).filter(Boolean);
  const partnerKey = partnerIds.join(',');
  useEffect(() => {
    if (!partnerKey) return;
    let active = true;
    const fetchPresence = async () => {
      try {
        const { data } = await axios.get(`${API}/presence`, { params: { ids: partnerKey } });
        if (active) { setPresence(data.online || {}); setLastSeen(data.last_seen || {}); }
      } catch (e) { /* ignore */ }
    };
    fetchPresence();
    const t = setInterval(fetchPresence, 20000);
    return () => { active = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerKey]);

  // Mark incoming messages as read when a conversation is opened.
  useEffect(() => {
    const conv = selectedConversation && conversations[selectedConversation.partnerId];
    if (!conv) return;
    const unread = conv.messages.filter((m) => !m.is_read && m.recipient_id === user?._id);
    if (unread.length === 0) return;
    Promise.all(unread.map((m) => axios.put(`${API}/messages/${m.id}/read`).catch(() => {})))
      .then(() => fetchMessages());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.partnerId]);

  const handleSendReply = async () => {
    if ((!replyText.trim() && !pendingImage && !pendingFile) || !selectedConversation) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: selectedConversation.partnerId,
        content: replyText,
        image_url: pendingImage || undefined,
        file_url: pendingFile?.url || undefined,
        file_name: pendingFile?.name || undefined,
      });
      setReplyText('');
      setPendingImage(null);
      setPendingFile(null);
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // Owner composes & sends a priced invoice into the open thread.
  const sendInvoice = async () => {
    const amt = parseFloat(invAmount);
    if (!amt || amt <= 0) { toast.error('Ievadi summu'); return; }
    if (!invDesc.trim()) { toast.error('Ievadi aprakstu'); return; }
    if (!selectedConversation) return;
    setInvSubmitting(true);
    try {
      await axios.post(`${API}/messages/invoice`, {
        recipient_id: selectedConversation.partnerId,
        amount: amt,
        description: invDesc.trim(),
      });
      setInvoiceOpen(false); setInvAmount(''); setInvDesc('');
      toast.success('Rēķins nosūtīts');
      fetchMessages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Neizdevās nosūtīt rēķinu');
    } finally { setInvSubmitting(false); }
  };

  // Recipient pays an invoice → Stripe checkout (returns to /messages).
  const payInvoice = async (msg) => {
    setPayingId(msg.id);
    try {
      const { data } = await axios.post(`${API}/messages/invoice/${msg.id}/pay`, { origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Neizdevās sākt maksājumu');
      setPayingId(null);
    }
  };

  // Recipient pays an invoice with their KoLV wallet (settled immediately).
  const payInvoiceWallet = async (msg) => {
    setPayingId(msg.id);
    try {
      await axios.post(`${API}/messages/invoice/${msg.id}/pay`, { method: 'wallet' });
      toast.success('Rēķins apmaksāts ar maciņu!');
      setWalletBalance((b) => (b != null ? b - Number(msg.invoice?.amount || 0) : b));
      fetchMessages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Neizdevās apmaksāt');
    } finally { setPayingId(null); }
  };

  // Returning from Stripe after paying an invoice in chat → confirm & refresh.
  useEffect(() => {
    if (!user) return;
    if (searchParams.get('invoice_cancelled')) { toast.error('Maksājums atcelts'); return; }
    const sid = searchParams.get('session_id');
    if (!searchParams.get('invoice_paid') || !sid) return;
    let attempt = 0; let active = true;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/messages/invoice/status/${sid}`);
        if (data.payment_status === 'paid') { if (active) { toast.success('Rēķins apmaksāts!'); fetchMessages(); } return; }
      } catch (e) { /* keep polling */ }
      if (active && ++attempt < 8) setTimeout(poll, 1500);
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    if (!isImage && !isPdf) { toast.error(language === 'lv' ? 'Atļauti attēli un PDF' : 'Images and PDF only'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error(language === 'lv' ? 'Fails pārāk liels (maks. 10MB)' : 'File too large (max 10MB)'); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (isPdf) { setPendingFile({ url: data.url, name: data.name || file.name }); setPendingImage(null); }
      else { setPendingImage(data.url); setPendingFile(null); }
    } catch (err) {
      toast.error(language === 'lv' ? 'Neizdevās augšupielādēt' : 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  // Relative "last seen" label in the chat header (lv/en).
  const lastSeenLabel = (iso) => {
    if (!iso) return null;
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const mins = Math.floor(diff / 60000);
    if (language === 'lv') {
      if (mins < 1) return 'pēdējoreiz redzēts tikko';
      if (mins < 60) return `pēdējoreiz redzēts pirms ${mins} min`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `pēdējoreiz redzēts pirms ${hrs} h`;
      const days = Math.floor(hrs / 24);
      return `pēdējoreiz redzēts pirms ${days} d`;
    }
    if (mins < 1) return 'last seen just now';
    if (mins < 60) return `last seen ${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `last seen ${hrs}h ago`;
    return `last seen ${Math.floor(hrs / 24)}d ago`;
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString(language === 'lv' ? 'lv-LV' : 'en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return language === 'lv' ? 'Vakar' : 'Yesterday';
    } else {
      return date.toLocaleDateString(language === 'lv' ? 'lv-LV' : 'en-US', { day: 'numeric', month: 'short' });
    }
  };

  const selectedMessages = selectedConversation?.messages?.sort((a, b) => 
    new Date(a.created_at) - new Date(b.created_at)
  ) || [];

  return (
    <div className="min-h-screen relative" style={{ background: '#0A0D0B' }} data-testid="messages-page">
      {/* Ambient glow + noise to bind the dark canvas */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        background: 'radial-gradient(80% 60% at 80% 0%, rgba(255,159,28,0.10), transparent 60%), radial-gradient(70% 50% at 0% 100%, rgba(46,196,182,0.08), transparent 60%)',
      }} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="flex items-center gap-3 mb-7">
          <span className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-amber-400" />
          </span>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tighter text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {t('messages')}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Conversations List */}
          <div className={`rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden ${selectedConversation ? 'hidden lg:block' : ''}`}>
            <div className="px-5 py-4 border-b border-white/10">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-500">
                {language === 'lv' ? 'Sarunas' : 'Conversations'}
              </p>
            </div>
            <div className="max-h-[520px] overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-16 bg-white/5 rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : displayList.length > 0 ? (
                <motion.div initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}>
                  {displayList.map((conv) => {
                    const active = selectedConversation?.partnerId === conv.partnerId;
                    return (
                      <motion.button
                        key={conv.partnerId}
                        variants={{ hidden: { opacity: 0, x: -12 }, show: { opacity: 1, x: 0 } }}
                        onClick={() => setSelectedConversation(conv)}
                        className={`w-full px-4 py-3.5 text-left transition-colors border-b border-white/5 last:border-b-0 ${active ? 'bg-amber-500/10' : 'hover:bg-white/[0.05]'}`}
                        data-testid={`conversation-${conv.partnerId}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-500/50 to-teal-500/40 flex items-center justify-center text-white font-bold shrink-0 border-2 border-[#0A0D0B] relative">
                            {initials(conv.partnerName)}
                            {presence[conv.partnerId] && (
                              <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-400 border-2 border-[#0A0D0B]" data-testid={`online-dot-${conv.partnerId}`} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-semibold text-white truncate" style={{ fontFamily: 'Manrope' }}>{conv.partnerName}</span>
                              <span className="text-xs text-white/40 shrink-0">{formatTime(conv.lastMessage?.created_at)}</span>
                            </div>
                            <p className="text-sm text-white/50 truncate mt-0.5">{conv.lastMessage?.content || (conv.lastMessage?.image_url ? '📷' : '—')}</p>
                          </div>
                          {conv.unreadCount > 0 && (
                            <span className="w-5 h-5 bg-amber-500 text-[#0A0D0B] text-xs font-bold rounded-full flex items-center justify-center shrink-0">
                              {conv.unreadCount}
                            </span>
                          )}
                        </div>
                      </motion.button>
                    );
                  })}
                </motion.div>
              ) : (
                <div className="p-10 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-white/20" />
                  <p className="text-white/40">{t('noMessages')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Message Thread */}
          <div className={`lg:col-span-2 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden flex flex-col min-h-[560px] ${!selectedConversation ? 'hidden lg:flex' : ''}`}>
            {selectedConversation ? (
              <>
                {/* Header */}
                <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="lg:hidden text-white/60 hover:text-white -ml-1"
                    data-testid="thread-back-btn"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => navigate(`/lietotajs/${selectedConversation.partnerId}`)}
                    className="flex items-center gap-3 group text-left rounded-xl -mx-1 px-1 py-0.5 hover:bg-white/5 transition-colors"
                    title={language === 'lv' ? 'Skatīt publisko profilu' : 'View public profile'}
                    data-testid="thread-partner-profile-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500/50 to-teal-500/40 flex items-center justify-center text-white font-bold border-2 border-[#0A0D0B] relative">
                      {initials(selectedConversation.partnerName)}
                      {presence[selectedConversation.partnerId] && (
                        <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-400 border-2 border-[#0A0D0B]" />
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-semibold text-white leading-tight group-hover:text-amber-400 transition-colors" style={{ fontFamily: 'Manrope' }}>{selectedConversation.partnerName}</span>
                      {presence[selectedConversation.partnerId] ? (
                        <span className="text-xs text-green-400" data-testid="partner-online-label">{language === 'lv' ? 'tiešsaistē' : 'online'}</span>
                      ) : lastSeenLabel(lastSeen[selectedConversation.partnerId]) ? (
                        <span className="text-xs text-white/40" data-testid="partner-lastseen-label">{lastSeenLabel(lastSeen[selectedConversation.partnerId])}</span>
                      ) : (
                        <span className="text-xs text-white/40">{language === 'lv' ? 'Skatīt profilu' : 'View profile'}</span>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => { setInvAmount(''); setInvDesc(''); setInvoiceOpen(true); }}
                    className="ml-auto shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-semibold hover:bg-amber-500/25 transition-colors"
                    data-testid="create-invoice-btn"
                    title="Izveidot un nosūtīt rēķinu"
                  >
                    <Receipt className="w-4 h-4" /> Rēķins
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 px-4 sm:px-5 py-5 overflow-y-auto max-h-[420px] space-y-3">
                  <AnimatePresence initial={false}>
                    {selectedMessages.map((msg) => {
                      const isMine = msg.sender_id === user?._id;
                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ duration: 0.25 }}
                          className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                        >
                          {msg.invoice ? (
                            <div className={`max-w-[85%] rounded-2xl border p-4 ${isMine ? 'bg-amber-500/10 border-amber-500/30' : 'bg-white/[0.06] border-white/15'}`} data-testid={`invoice-msg-${msg.id}`}>
                              <div className="flex items-center gap-2 mb-1.5">
                                <Receipt className="w-4 h-4 text-amber-400" />
                                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">Rēķins</span>
                              </div>
                              <p className="text-white text-sm break-words" style={{ fontFamily: 'Manrope' }}>{msg.invoice.description}</p>
                              <p className="text-2xl font-light text-white mt-1">€{Number(msg.invoice.amount || 0).toFixed(2)}</p>
                              {msg.invoice.status === 'paid' ? (
                                <div className="mt-2.5 flex flex-wrap items-center gap-3">
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-400" data-testid={`invoice-paid-${msg.id}`}><Check className="w-3.5 h-3.5" /> Apmaksāts</span>
                                  <button onClick={() => window.open(`${API}/messages/invoice/${msg.id}/pdf`, '_blank')} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-400 hover:underline" data-testid={`invoice-pdf-${msg.id}`}><Download className="w-3.5 h-3.5" /> Rēķins (PDF)</button>
                                </div>
                              ) : !isMine ? (
                                <div className="mt-3 space-y-2">
                                  {walletBalance != null && walletBalance >= Number(msg.invoice.amount || 0) && (
                                    <button onClick={() => payInvoiceWallet(msg)} disabled={payingId === msg.id}
                                      className="w-full h-10 rounded-full bg-[#2F7E79] text-white font-bold text-sm hover:bg-[#276b66] disabled:opacity-50 flex items-center justify-center gap-2"
                                      data-testid={`pay-invoice-wallet-${msg.id}`}>
                                      {payingId === msg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />} Maksāt ar maciņu €{Number(msg.invoice.amount || 0).toFixed(2)}
                                    </button>
                                  )}
                                  <button onClick={() => payInvoice(msg)} disabled={payingId === msg.id}
                                    className="w-full h-10 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm hover:bg-amber-400 disabled:opacity-50 flex items-center justify-center gap-2"
                                    data-testid={`pay-invoice-${msg.id}`}>
                                    {payingId === msg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Maksāt ar karti €{Number(msg.invoice.amount || 0).toFixed(2)}
                                  </button>
                                </div>
                              ) : (
                                <span className="mt-2 inline-block text-xs text-white/50">Gaida apmaksu</span>
                              )}
                              <div className="text-[11px] text-white/40 mt-2">{formatTime(msg.created_at)}</div>
                            </div>
                          ) : (
                          <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl ${
                            isMine
                              ? 'bg-amber-500 text-[#0A0D0B] rounded-br-md'
                              : 'bg-white/[0.08] text-white border border-white/10 rounded-bl-md'
                          }`}>
                            {msg.image_url && (
                              <img
                                src={resolveAssetUrl(msg.image_url)}
                                alt="attachment"
                                className="rounded-xl mb-1 max-h-60 w-auto cursor-pointer object-cover"
                                onClick={() => window.open(resolveAssetUrl(msg.image_url), '_blank')}
                                data-testid="message-image"
                              />
                            )}
                            {msg.file_url && (
                              <button
                                onClick={() => window.open(resolveAssetUrl(msg.file_url), '_blank')}
                                className={`flex items-center gap-2 mb-1 rounded-xl px-3 py-2 text-left w-full ${isMine ? 'bg-black/10 hover:bg-black/20' : 'bg-white/10 hover:bg-white/15'}`}
                                data-testid="message-file"
                              >
                                <FileText className="w-5 h-5 shrink-0" />
                                <span className="text-xs font-semibold truncate flex-1">{msg.file_name || 'Fails.pdf'}</span>
                                <Download className="w-4 h-4 shrink-0 opacity-70" />
                              </button>
                            )}
                            {msg.content && <p className="text-sm whitespace-pre-wrap break-words" style={{ fontFamily: 'Manrope' }}>{msg.content}</p>}
                            <div className={`flex items-center justify-end gap-1 mt-1 ${isMine ? 'text-[#0A0D0B]/60' : 'text-white/40'}`}>
                              <span className="text-[11px]">{formatTime(msg.created_at)}</span>
                              {isMine && (msg.is_read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />)}
                            </div>
                          </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                  {selectedMessages.length === 0 && (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-white/30 text-sm">{language === 'lv' ? 'Sāc sarunu — uzraksti ziņu.' : 'Start the conversation — say hi.'}</p>
                    </div>
                  )}
                </div>

                {/* Reply Input */}
                <div className="px-4 sm:px-5 py-4 border-t border-white/10">
                  {pendingImage && (
                    <div className="relative inline-block mb-2" data-testid="pending-image-preview">
                      <img src={resolveAssetUrl(pendingImage)} alt="preview" className="h-20 rounded-lg object-cover" />
                      <button
                        onClick={() => setPendingImage(null)}
                        className="absolute -top-2 -right-2 bg-white text-[#0A0D0B] rounded-full p-0.5"
                        data-testid="remove-pending-image"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {pendingFile && (
                    <div className="inline-flex items-center gap-2 mb-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2" data-testid="pending-file-preview">
                      <FileText className="w-5 h-5 text-amber-400 shrink-0" />
                      <span className="text-xs text-white truncate max-w-[180px]">{pendingFile.name}</span>
                      <button onClick={() => setPendingFile(null)} className="text-white/60 hover:text-white" data-testid="remove-pending-file">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2 items-end">
                    <label className="shrink-0 h-11 w-11 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors" data-testid="attach-image-btn" title={language === 'lv' ? 'Pievienot attēlu vai PDF' : 'Attach image or PDF'}>
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleImagePick} disabled={uploadingImage} data-testid="image-file-input" />
                      <ImagePlus className={`w-5 h-5 ${uploadingImage ? 'text-amber-400 animate-pulse' : 'text-white/50'}`} />
                    </label>
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={t('typeMessage')}
                      rows={1}
                      className="resize-none bg-white/5 border border-white/10 text-white placeholder-white/40 rounded-2xl focus:border-amber-500/60 focus-visible:ring-0"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendReply();
                        }
                      }}
                      data-testid="reply-input"
                    />
                    <button
                      onClick={handleSendReply}
                      disabled={sending || uploadingImage || (!replyText.trim() && !pendingImage && !pendingFile)}
                      className="shrink-0 h-11 w-11 flex items-center justify-center rounded-2xl bg-amber-500 text-[#0A0D0B] hover:bg-amber-400 transition-colors disabled:opacity-40"
                      style={{ boxShadow: '0 8px 24px rgba(255,159,28,0.3)' }}
                      data-testid="send-reply-btn"
                    >
                      {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center p-10">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-white/15" />
                  <p className="text-white/40">
                    {language === 'lv' ? 'Izvēlies sarunu' : 'Select a conversation'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-white w-[92vw] sm:w-full max-w-md" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Izveidot rēķinu</DialogTitle>
            <DialogDescription className="text-white/50">
              Nosūti rēķinu {selectedConversation?.partnerName || 'klientam'}. Klients to apmaksās online caur KoLV (komisija 10%).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-white/60 text-xs font-semibold">Summa (€)</label>
              <Input type="number" min="0" step="0.01" value={invAmount} onChange={(e) => setInvAmount(e.target.value)}
                placeholder="0.00" className="bg-white/5 border-white/10 text-white mt-1" data-testid="invoice-amount-input" />
            </div>
            <div>
              <label className="text-white/60 text-xs font-semibold">Apraksts</label>
              <Input value={invDesc} onChange={(e) => setInvDesc(e.target.value)}
                placeholder="Piem. Konsultācija, piegāde…" className="bg-white/5 border-white/10 text-white mt-1" data-testid="invoice-desc-input" />
            </div>
            <button onClick={sendInvoice} disabled={invSubmitting}
              className="w-full h-11 rounded-full bg-amber-500 text-[#0A0D0B] font-bold hover:bg-amber-400 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="invoice-send-btn">
              {invSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Nosūtīt rēķinu
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
